/**
 * Moving a working stop through the market (audit AB-3 follow-up). Pure, no protobuf runtime.
 *
 * A stop (stop market / stop limit, judged by its trigger) rests on the far side of the market:
 * a buy stop above it, a sell stop below it. Dragging one to or through the market triggers it at
 * once, as a market or limit order according to its type. The gateway refuses that for an order that adds exposure (an entry
 * stop, `STOP_WRONG_SIDE`, #19) but lets a protective stop through: it only exits. The UI asks
 * before sending such a move, so an exit at market is never a surprise.
 *
 * Protective, as the gateway classifies a modify (`modify_increases`, simplified to the net
 * position): a bracket child or a risk liquidation, or an order on the opposite side of the net
 * position that is no larger than it. Anything else is an entry stop, which the gateway refuses;
 * the UI sends it and shows the refusal.
 */
import type { OrderEvent } from "../gen/helm/v1/trading_pb";
import { PB_BRACKET_ROLE, PB_ORDER_TYPE, PB_ORIGIN_BRACKET, PB_SIDE } from "./orderEntry.ts";

/** Mirrors proto `ORIGIN_RISK` (a risk liquidation the gateway placed). */
export const PB_ORIGIN_RISK = 7;

/** The order fields the check reads (`OrderEvent`, structurally). */
export type StopMoveOrder = Pick<
  OrderEvent,
  "side" | "type" | "origin" | "parentId" | "bracketRole" | "qty" | "filled" | "stopTicks"
>;

/** The market in integer ticks; null when unknown. */
export type StopMoveMarket = { bid: number | null; ask: number | null; last: number | null };

export type StopMoveCheck =
  /** Not a stop, the stop does not move, it stays on the far side, or the market is unknown. */
  | { kind: "ok" }
  /** A protective stop moved through the market; a stop limit may remain unfilled. */
  | {
      kind: "protective";
      side: "buy" | "sell";
      qty: number;
      stopTicks: number;
      marketTicks: number;
      stopLimit: boolean;
    }
  /** An entry stop moved to or through the market: the gateway refuses it (`STOP_WRONG_SIDE`). */
  | { kind: "entry"; side: "buy" | "sell"; stopTicks: number; marketTicks: number };

const num = (v: number | bigint): number => Number(v);

function isStopType(type: number): boolean {
  return type === PB_ORDER_TYPE.STOP_MARKET || type === PB_ORDER_TYPE.STOP_LIMIT;
}

/** Contracts still working. */
function remainingOf(o: Pick<StopMoveOrder, "qty" | "filled">): number {
  return Math.max(0, num(o.qty) - num(o.filled));
}

/**
 * Does working order `o` only protect or close, given the account's net position in its contract
 * (`netQty`, signed)? Bracket children (and position brackets, which carry a bracket role) and
 * risk liquidations always do; any other order does when it is on the opposite side of the
 * position and no larger than it.
 */
export function isProtectiveOrder(o: StopMoveOrder, netQty: number): boolean {
  const origin = num(o.origin);
  if (origin === PB_ORIGIN_BRACKET || origin === PB_ORIGIN_RISK) return true;
  const role = num(o.bracketRole);
  if (role === PB_BRACKET_ROLE.STOP || role === PB_BRACKET_ROLE.TARGET) return true;
  if (netQty === 0) return false;
  const sign = num(o.side) === PB_SIDE.BUY ? 1 : -1;
  return sign === -Math.sign(netQty) && remainingOf(o) <= Math.abs(netQty);
}

/**
 * The price a `side` stop at `stopTicks` is judged against, and whether it is at or through it.
 * Conservative: a buy stop against the higher of the ask and the last trade, a sell stop against
 * the lower of the bid and the last trade. Null when neither is known.
 */
export function stopThroughMarket(
  side: "buy" | "sell",
  stopTicks: number,
  m: StopMoveMarket,
): { through: boolean; marketTicks: number } | null {
  const refs = (side === "buy" ? [m.ask, m.last] : [m.bid, m.last]).filter(
    (v): v is number => v !== null && Number.isFinite(v),
  );
  if (refs.length === 0) return null;
  if (side === "buy") {
    const ref = Math.max(...refs);
    return { through: stopTicks <= ref, marketTicks: ref };
  }
  const ref = Math.min(...refs);
  return { through: stopTicks >= ref, marketTicks: ref };
}

/**
 * What moving working order `o`'s stop to `newStopTicks` means. `netQty`: the account's signed net
 * position in the order's contract.
 */
export function checkStopMove(
  o: StopMoveOrder,
  newStopTicks: number | undefined,
  netQty: number,
  m: StopMoveMarket,
): StopMoveCheck {
  if (!isStopType(num(o.type)) || newStopTicks === undefined) return { kind: "ok" };
  if (newStopTicks === num(o.stopTicks)) return { kind: "ok" };
  const side = num(o.side) === PB_SIDE.BUY ? "buy" : "sell";
  const t = stopThroughMarket(side, newStopTicks, m);
  if (t === null || !t.through) return { kind: "ok" };
  return isProtectiveOrder(o, netQty)
    ? {
        kind: "protective",
        side,
        qty: remainingOf(o),
        stopTicks: newStopTicks,
        marketTicks: t.marketTicks,
        stopLimit: num(o.type) === PB_ORDER_TYPE.STOP_LIMIT,
      }
    : { kind: "entry", side, stopTicks: newStopTicks, marketTicks: t.marketTicks };
}

/** The confirmation text for a protective stop moved through the market. */
export function protectiveStopWords(
  c: Extract<StopMoveCheck, { kind: "protective" }>,
  symbol: string,
  price: (ticks: number) => string,
): string {
  const side = c.side === "buy" ? "Buy" : "Sell";
  const where = c.side === "buy" ? "at or below" : "at or above";
  const n = c.qty === 1 ? "1 contract" : `${String(c.qty)} contracts`;
  return (
    `${side} stop ${symbol} to ${price(c.stopTicks)} is ${where} the market (${price(c.marketTicks)}): ` +
    (c.stopLimit
      ? `it triggers a limit order for ${n}, which may remain unfilled. The position may stay open.`
      : `it triggers immediately and exits ${n} at market.`)
  );
}
